/**
 * Bouton « favori » réutilisable (fiche produit, listes). Intégration dans la fiche produit d'A2 : par A0.
 * Masqué sans la permission `favoris.gerer`. Bascule ajout / retrait pour un couple produit + conditionnement.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, useToast } from '@myron/design-system';
import { Heart } from 'lucide-react';
import { del, messageErreur, post } from '../../../shared/api/client';
import { useSession } from '../../../shared/auth/session';
import { clesFidelite, useFavoris, type Favori, type TypeCond } from './api';

export function BoutonFavori({ produitId, conditionnementType, libelle, compact = true }: { produitId: string; conditionnementType: TypeCond; libelle?: string; compact?: boolean }) {
  const { peut } = useSession();
  const autorise = peut('favoris.gerer');
  const favoris = useFavoris(autorise);
  const qc = useQueryClient();
  const toast = useToast();
  const existant = favoris.data?.find((f) => f.produitId === produitId && f.conditionnementType === conditionnementType);
  const basculer = useMutation({
    mutationFn: async () => (existant ? del(`/pro/favoris/${existant.id}`) : post<Favori>('/pro/favoris', { produitId, conditionnementType })),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: clesFidelite.favoris }); toast({ ton: 'succes', titre: existant ? 'Retiré des favoris' : 'Ajouté aux favoris' }); },
    onError: (e) => toast({ ton: 'erreur', titre: 'Favori non modifié', texte: messageErreur(e) }),
  });
  if (!autorise) return null;
  const nom = libelle ? ` : ${libelle}` : '';
  return (
    <Button
      variante={existant ? 'secondaire' : 'tertiaire'}
      compact={compact}
      icone={Heart}
      aria-pressed={!!existant}
      aria-label={`${existant ? 'Retirer des favoris' : 'Ajouter aux favoris'}${nom}`}
      chargement={basculer.isPending}
      disabled={favoris.isPending}
      onClick={() => basculer.mutate()}
    >
      {existant ? 'Favori' : 'Ajouter aux favoris'}
    </Button>
  );
}
